import { datadogLogs } from '@datadog/browser-logs';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { getIsAdvertiserEmailUnverified } from 'src/app/utils/getIsAdvertiserEmailUnverified';
import { log } from 'src/app/utils/logging';
import { actionUpdateHirerAccount } from 'src/app/v1/state/modules/session/hirerAccount';
import { actionUpdatePriceVisibility } from 'src/app/v1/state/modules/tealium/priceVisibility';
import {
  AccountActivationStatus,
  useGetEmailVerificationInAdcentreLazyQuery,
  useTriggerActivationMutation,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';

export const useEmailVerificationSync = () => {
  const { hirerAccount, seekAdvertiserId } = useSelector(
    (state: AppState) => ({
      hirerAccount: state.session.hirerAccount,
      seekAdvertiserId: state.session.seekAdvertiserId,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();

  const [triggerActivationMutation] = useTriggerActivationMutation({
    onCompleted: (emailVerificationInAdcentreData) => {
      datadogLogs.logger.info(
        'useHandleEmailVerification | triggerActivationMutation has returned',
        {
          emailVerificationInAdcentreData,
        },
      );

      if (!emailVerificationInAdcentreData.triggerActivation.success) {
        log.error(
          'useHandleEmailVerification | triggerActivationMutation returned unsuccessful response',
        );
      }
    },
    onError: (error) => {
      log.error(
        'useHandleEmailVerification | triggerActivationMutation failed',
        {
          error,
        },
      );
    },
  });

  const [getEmailVerificationInAdcentreLazyQuery] =
    useGetEmailVerificationInAdcentreLazyQuery({
      variables: { advertiserId: Number(seekAdvertiserId) },
      fetchPolicy: 'no-cache',
      onCompleted: (emailVerificationInAdcentreData) => {
        if (
          getIsAdvertiserEmailUnverified(
            emailVerificationInAdcentreData.hirerAccount?.activationStatus,
          )
        ) {
          triggerActivationMutation();
        } else {
          dispatch(
            actionUpdateHirerAccount({
              ...hirerAccount,
              activationStatus: AccountActivationStatus.ACTIVE,
            }),
          );
          dispatch(
            actionUpdatePriceVisibility(
              hirerAccount.hidePrice ? 'hidden' : 'shown',
            ),
          );
        }
      },
      onError: (error) => {
        log.error(
          'useHandleEmailVerification | getEmailVerificationInAdcentreLazyQuery failed',
          {
            error,
            seekAdvertiserId,
          },
        );
      },
    });

  return { emailVerificationSync: getEmailVerificationInAdcentreLazyQuery };
};
